<template>
  <lf-checkbox-dropdown
    class="z-30 min-w-full"
    lazy-load
    enable-search
    name="naics_codes"
    show-clear-filter
    width-full
    dropdown-width-full
    no-margin
    :options="naicsCodes"
    :placeholder="$t('DEALS.TABLE_HEADERS.NAICS_CODES')"
    :is-loading="loading"
    :filter-icon="filterIcon"
    :toggle-from-parent="toggleFromParent"
    :model-value="formattedModelValue"
    :option-formatter="
      (value: string) => truncateWithEllipsis(value, charLimit)
    "
    :get-paginated-options="getNaicsCodes"
    @update:model-value="
      (value: unknown[]) => handleModelValueUpdate(value as string[])
    "
  />
</template>
<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { truncateWithEllipsis } from "@/helpers/formatting";
import { usePromiseWrapper } from "@/hooks/common";

import LfCheckboxDropdown from "@/components/ui/inputs/LfCheckboxDropdown.vue";

const {
  modelValue,
  toggleFromParent = false,
  filterIcon = false,
  loadOnMount = false,
} = defineProps<{
  modelValue: string[];
  toggleFromParent?: boolean;
  filterIcon?: boolean;
  loadOnMount?: boolean;
}>();

const emit = defineEmits<{
  "update:model-value": [value: string[]];
}>();

const { getters, dispatch } = useStore();
const { t } = useI18n();

const charLimit = computed(() => (loadOnMount ? 30 : 20));

const naicsCodes = computed<Record<string, string>>(() => {
  const codesOptions: Record<string, string> =
    getters["options/naicsCodesOptions"];

  return Object.entries(codesOptions).reduce(
    (acc: Record<string, string>, [key, value]) => {
      acc[value] = key;
      return acc;
    },
    { "0": t("COMMON.NONE") }
  );
});

const formattedModelValue = computed(() => {
  return modelValue.map((item) => (item === "null" ? "0" : item));
});

const { fetchWrapper: getNaicsCodes, loading } = usePromiseWrapper(async () => {
  await dispatch("options/getNaicsCodes");
});

onMounted(async () => {
  if (loadOnMount) {
    await getNaicsCodes();
  }
});

const handleModelValueUpdate = (value: string[]) => {
  emit(
    "update:model-value",
    value.map((item) => (item === "0" ? "null" : item))
  );
};
</script>
